import { initContract } from '@ts-rest/core';
import { z } from 'zod';
import { FeatureNameSchema, FeatureStatusSchema, } from './workspaceFeature';
const c = initContract();
// ── Plan Tier (source of truth) ──
export const PlanTierSchema = z.enum(['free', 'pro', 'enterprise']);
export const TIER_FEATURES = {
    free: ['chat', 'regulatoryWatch', 'mcp'],
    pro: ['chat', 'regulatoryWatch', 'tableView', 'literatureReview', 'mcp'],
    enterprise: [
        'chat',
        'regulatoryWatch',
        'tableView',
        'literatureReview',
        'mcp',
    ],
};
// ── Regulatory Watch Enums ──
// Note: REGULATORY_SOURCES enum removed — sources are now dynamic from DB via /regulatory-watch/sources
// Note: GEOGRAPHIES / DISPLAYED_GEOGRAPHIES removed — geographies are now dynamic from DB sources
export const SOURCE_TYPES = [
    'regulation',
    'guidance',
    'standard',
    'news',
    'unknown',
];
export const UPDATE_TYPES = [
    'new',
    'revision',
    'withdrawal',
    'status_change',
    'unknown',
];
export const VERSION_STATUSES = [
    'draft',
    'final',
    'withdrawn',
    'unknown',
];
export const APPLICABILITY_STATUSES = [
    'to_review',
    'applicable',
    'not_applicable',
    'out_of_scope',
];
export const IMPLEMENTATION_STATUSES = ['no', 'in_progress', 'yes'];
export const PRIORITIES = ['urgent', 'high', 'medium', 'low', 'none'];
export const REDLINE_STATUSES = [
    'none',
    'generating',
    'completed',
    'failed',
];
export const SUBSCRIPTION_STATUSES = [
    'subscribed',
    'unsubscribed',
    'unknown',
];
export const UPDATE_KINDS = ['regulatory_update', 'news'];
export const WEBPAGE_TYPES = ['feed', 'listing', 'single-document'];
// ── Source Scraping: Draft types (mirror DB models) ──
export const DraftFileSchema = z.object({
    name: z.string(),
    key: z.string(),
    bucket: z.string(),
    mimeType: z.string(),
    size: z.number(),
});
export const DraftVersionSchema = z.object({
    status: z.enum(['draft', 'final', 'withdrawn', 'unknown']),
    url: z.string().nullable(),
    effectiveDate: z.string().nullable(),
    metadata: z.record(z.unknown()).nullable(),
    file: DraftFileSchema.nullable(),
});
export const DraftUpdateSchema = z.object({
    kind: z.enum(['regulatory_update', 'news']),
    updateType: z
        .enum(['new', 'revision', 'withdrawal', 'status_change', 'unknown'])
        .nullable(),
    shortSummary: z.string().nullable(),
    summary: z.string().nullable(),
    publicationDate: z.string(),
    title: z.string().nullable(),
    content: z.string().nullable(),
    articleUrl: z.string().nullable(),
});
export const DraftSourceSchema = z.object({
    id: z.string(),
    title: z.string(),
    description: z.string().nullable(),
    geography: z.string(),
    url: z.string(),
    webpageType: z.enum(['feed', 'listing', 'single-document']),
});
export const DraftReferenceSchema = z.object({
    sourceId: z.string(),
    externalId: z.string(),
    title: z.string(),
    description: z.string().nullable(),
    url: z.string().nullable(),
    type: z
        .enum(['regulation', 'guidance', 'standard', 'news', 'unknown'])
        .nullable()
        .optional(),
    version: DraftVersionSchema,
    update: DraftUpdateSchema.nullable(),
});
export const DiscardedFeedItemSchema = z.object({
    sourceId: z.string(),
    url: z.string(),
    publicationDate: z.string(),
    reason: z.string().optional(),
});
export const DiscoveryResultSchema = z.object({
    source: DraftSourceSchema,
    references: z.array(DraftReferenceSchema),
    discardedItems: z.array(DiscardedFeedItemSchema).optional(),
});
export const CompletedStepSchema = z.object({
    step: z.string(),
    message: z.string(),
    output: z.string().nullable(),
});
export const SourceScrapingProgressSchema = z.object({
    step: z.string(),
    message: z.string(),
    completedSteps: z.array(CompletedStepSchema),
});
export const SCRAPING_STEPS = {
    feed: [
        { step: 'infer_sources', label: 'Inferring source metadata' },
        { step: 'scrape_articles', label: 'Scraping feed items list' },
        { step: 'classify_articles', label: 'Classifying feed items' },
        { step: 'enrich_articles', label: 'Scraping feed items details' },
    ],
    listing: [
        { step: 'infer_sources', label: 'Inferring source metadata' },
        { step: 'discover_references', label: 'Discovering references' },
        { step: 'enrich_references', label: 'Enriching references' },
    ],
    'single-document': [
        { step: 'infer_sources', label: 'Inferring source metadata' },
        { step: 'enrich_document', label: 'Enriching document' },
    ],
};
// ── Source Scraping: Request/Response schemas ──
export const StartSourceScrapingBodySchema = z.object({
    url: z.string().url(),
    webpageType: z.enum(['feed', 'listing', 'single-document']),
    cutoffDate: z.string(),
});
export const StartSourceScrapingResponseSchema = z.object({
    workflowId: z.string(),
});
export const GetSourceScrapingStatusResponseSchema = z.object({
    workflowStatus: z.enum([
        'RUNNING',
        'COMPLETED',
        'FAILED',
        'CANCELLED',
        'UNKNOWN',
    ]),
    progress: SourceScrapingProgressSchema.nullable(),
    result: DiscoveryResultSchema.nullable(),
    error: z.string().nullable(),
});
export const DiscardedItemInputSchema = z.object({
    sourceId: z.string(),
    url: z.string(),
    publicationDate: z.string(),
    reason: z.string().optional(),
});
export const ConfirmSourceScrapingBodySchema = z.object({
    source: DraftSourceSchema,
    references: z.array(DraftReferenceSchema),
    discardedItems: z.array(DiscardedItemInputSchema).optional(),
});
export const ConfirmSourceScrapingResponseSchema = z.object({
    sourceIds: z.array(z.string()),
    referencesCreated: z.number(),
    updatesCreated: z.number(),
});
// ── Discarded Source Items ──
export const DiscardedSourceItemResponseSchema = z.object({
    id: z.string(),
    sourceId: z.string(),
    sourceTitle: z.string(),
    url: z.string(),
    publicationDate: z.string(),
    reason: z.string().nullable(),
    createdAt: z.string(),
});
export const ListDiscardedItemsQuerySchema = z.object({
    source: z.string().optional(),
});
// User Profile DTO for back office (reusing from user contract)
export const BackOfficeUserProfileSchema = z.object({
    id: z.string(),
    firstName: z.string().optional(),
    lastName: z.string().optional(),
    email: z.string().optional(),
    locale: z.string().optional(),
    type: z.enum(['ditto', 'client', 'consultant']).optional(),
    workspaceIds: z.array(z.string()).optional(),
    workspaceCount: z.number().optional(),
    createdAt: z.string().datetime().optional(),
});
// ── Bulk Delete Workspaces ──
export const BulkDeleteWorkspacesBodySchema = z.object({
    ids: z.array(z.string()),
});
export const BulkDeleteWorkspacesResponseSchema = z.object({
    message: z.string(),
    deletedCount: z.number(),
});
// ── Bulk Delete Users ──
export const BulkDeleteUsersBodySchema = z.object({
    ids: z.array(z.string()),
});
export const BulkDeleteUsersResponseSchema = z.object({
    message: z.string(),
    deletedCount: z.number(),
});
// Workspace Feature DTO for back office
export const BackOfficeWorkspaceFeatureSchema = z.object({
    id: z.string(),
    workspaceId: z.string(),
    feature: FeatureNameSchema,
    status: FeatureStatusSchema,
    createdAt: z.string().datetime(),
    updatedAt: z.string().datetime(),
});
// Workspace DTO for back office (reusing from workspace contract)
export const BackOfficeWorkspaceSchema = z.object({
    id: z.string(),
    name: z.string(),
    uname: z.string(),
    createdAt: z.string().datetime(),
    updatedAt: z.string().datetime(),
    features: z.array(BackOfficeWorkspaceFeatureSchema).optional(),
    memberCount: z.number(),
    tier: PlanTierSchema,
});
const UpdateUserTypeSchema = z.object({
    id: z.string(),
    type: z.enum(['ditto', 'client', 'consultant']),
});
const CreateUserSchema = z.object({
    firstName: z.string(),
    lastName: z.string(),
    email: z.string().email(),
    locale: z.string(),
    type: z.enum(['ditto', 'client', 'consultant']),
    createWorkspace: z
        .object({
        name: z.string(),
        uname: z.string(),
        features: z
            .array(z.object({
            feature: z.string(),
            status: z.string(),
        }))
            .optional(),
    })
        .optional(),
});
const UpdateUserWorkspacesSchema = z.object({
    userId: z.string(),
    workspaceIds: z.array(z.string()),
});
// ── Regulatory Source Item ──
export const InstitutionItemSchema = z.object({
    id: z.string(),
    name: z.string(),
    geographies: z.array(z.string()),
    createdAt: z.string(),
    updatedAt: z.string(),
});
export const AdminCreateInstitutionBodySchema = z.object({
    name: z.string(),
    geographies: z.array(z.string()),
});
export const AdminUpdateInstitutionBodySchema = z.object({
    name: z.string().optional(),
    geographies: z.array(z.string()).optional(),
});
// ── Consultant Organization ──
export const ConsultantOrganizationItemSchema = z.object({
    id: z.string(),
    name: z.string(),
    logo: z.string().nullable(),
    memberCount: z.number(),
    workspaceCount: z.number(),
    memberIds: z.array(z.string()),
    workspaceIds: z.array(z.string()),
    createdAt: z.string(),
    updatedAt: z.string(),
});
export const AdminCreateConsultantOrgBodySchema = z.object({
    name: z.string(),
});
export const AdminUpdateConsultantOrgBodySchema = z.object({
    name: z.string().optional(),
    memberIds: z.array(z.string()).optional(),
    workspaceIds: z.array(z.string()).optional(),
});
export const RegulatorySourceItemSchema = z.object({
    id: z.string(),
    title: z.string(),
    description: z.string().nullable(),
    geography: z.string(),
    institutionId: z.string().nullable(),
    institutionName: z.string().nullable(),
    institutionGeographies: z.array(z.string()),
    url: z.string().nullable(),
    webpageType: z.string().nullable(),
    icon: z.string().nullable(),
    createdAt: z.string(),
    updatedAt: z.string(),
});
// ── Admin Regulatory Watch: Sources ──
export const AdminCreateSourceBodySchema = z.object({
    id: z.string(),
    title: z.string(),
    description: z.string().optional(),
    geography: z.string().optional(),
    institutionId: z.string().nullable().optional(),
    url: z.string().optional(),
    webpageType: z.string().nullable().optional(),
    icon: z.string().optional(),
});
export const AdminUpdateSourceBodySchema = z.object({
    title: z.string().optional(),
    description: z.string().nullable().optional(),
    geography: z.string().optional(),
    institutionId: z.string().nullable().optional(),
    url: z.string().nullable().optional(),
    webpageType: z.string().nullable().optional(),
    icon: z.string().nullable().optional(),
});
// ── Admin Regulatory Watch: References ──
export const AdminReferenceItemSchema = z.object({
    id: z.string(),
    externalId: z.string(),
    title: z.string(),
    type: z.string().nullable(),
    source: z.object({
        id: z.string(),
        title: z.string(),
        geography: z.string(),
        icon: z.string().nullable(),
    }),
    url: z.string().nullable(),
    description: z.string().nullable(),
    createdAt: z.string(),
    updatedAt: z.string(),
    versionsCount: z.number(),
    updatesCount: z.number(),
    lastUpdatePublicationDate: z.string().nullable(),
});
export const AdminListReferencesQuerySchema = z.object({
    search: z.string().optional(),
    source: z.string().optional(),
});
// ── Admin Regulatory Watch: Create Reference ──
export const AdminCreateReferenceBodySchema = z.object({
    externalId: z.string(),
    title: z.string(),
    type: z.string().optional(),
    source: z.string(), // source ID
    url: z.string().optional(),
    description: z.string().optional(),
});
// ── Admin Regulatory Watch: Update Reference ──
export const AdminUpdateReferenceBodySchema = z.object({
    title: z.string().optional(),
    type: z.string().nullable().optional(),
    source: z.string().optional(), // source ID
    url: z.string().nullable().optional(),
    description: z.string().nullable().optional(),
});
// ── Admin Regulatory Watch: Updates ──
export const AdminUpdateItemSchema = z.object({
    id: z.string(),
    kind: z.string(),
    updateType: z.string().nullable(),
    shortSummary: z.string().nullable(),
    summary: z.string().nullable(),
    publicationDate: z.string(),
    referenceId: z.string().nullable(),
    referenceTitle: z.string().nullable(),
    referenceExternalId: z.string().nullable(),
    referenceSource: z.string().nullable(),
    versionId: z.string().nullable(),
    redlineStatus: z.string(),
    createdAt: z.string(),
    sharedFile: z
        .object({
        id: z.string(),
        name: z.string(),
        mimeType: z.string(),
        size: z.number(),
        signedUrl: z.string(),
    })
        .nullable(),
    // News fields
    sourceId: z.string().nullable(),
    sourceTitle: z.string().nullable(),
    articleUrl: z.string().nullable(),
    title: z.string().nullable(),
    content: z.string().nullable(),
    // Source metadata (for flags)
    sourceGeography: z.string().nullable(),
    sourceIcon: z.string().nullable(),
});
export const AdminListUpdatesQuerySchema = z.object({
    search: z.string().optional(),
    source: z.string().optional(),
    updateType: z.string().optional(),
    kind: z.string().optional(),
});
export const AdminCreateUpdateBodySchema = z.object({
    kind: z.string().optional(), // defaults to 'regulatory_update'
    // Regulatory update fields
    referenceId: z.string().optional(),
    versionId: z.string().optional(),
    updateType: z.string().optional(),
    // News fields
    sourceId: z.string().optional(),
    articleUrl: z.string().optional(),
    title: z.string().optional(),
    content: z.string().optional(),
    // Shared fields
    shortSummary: z.string().optional(),
    summary: z.string().optional(),
    publicationDate: z.string().optional(),
});
export const AdminUpdateUpdateBodySchema = z.object({
    shortSummary: z.string().optional(),
    summary: z.string().optional(),
    publicationDate: z.string().optional(),
    versionId: z.string().nullable().optional(),
    updateType: z.string().optional(),
    articleUrl: z.string().optional(),
    title: z.string().optional(),
    content: z.string().optional(),
});
// ── Admin Update Detail (single update with versions + redline) ──
export const AdminUpdateDetailVersionSchema = z.object({
    id: z.string(),
    status: z.string(),
    effectiveDate: z.string().nullable(),
    url: z.string().nullable(),
    sharedFile: z
        .object({
        id: z.string(),
        name: z.string(),
        signedUrl: z.string(),
    })
        .nullable(),
});
export const AdminUpdateDetailResponseSchema = z.object({
    id: z.string(),
    kind: z.string(),
    updateType: z.string().nullable(),
    shortSummary: z.string().nullable(),
    summary: z.string().nullable(),
    publicationDate: z.string(),
    createdAt: z.string(),
    referenceId: z.string().nullable(),
    referenceTitle: z.string().nullable(),
    referenceExternalId: z.string().nullable(),
    referenceSource: z.string().nullable(),
    referenceUrl: z.string().nullable(),
    versionId: z.string().nullable(),
    currentVersion: AdminUpdateDetailVersionSchema.nullable(),
    previousVersion: AdminUpdateDetailVersionSchema.nullable(),
    redline: z.object({
        html: z.string().nullable(),
        status: z.string(),
        excelFileUrl: z.string().nullable(),
    }),
    sharedFile: z
        .object({
        id: z.string(),
        name: z.string(),
        mimeType: z.string(),
        size: z.number(),
        signedUrl: z.string(),
    })
        .nullable(),
    // News fields
    sourceId: z.string().nullable(),
    sourceTitle: z.string().nullable(),
    articleUrl: z.string().nullable(),
    title: z.string().nullable(),
    content: z.string().nullable(),
    // Source metadata (for flags)
    sourceGeography: z.string().nullable(),
    sourceIcon: z.string().nullable(),
});
export const AdminGenerateRedlineResponseSchema = z.object({
    jobId: z.string().nullable(),
});
export const AdminGenerateSummaryResponseSchema = z.object({
    shortSummary: z.string().nullable(),
    summary: z.string().nullable(),
});
// ── Admin Regulatory Watch: Versions ──
export const AdminVersionItemSchema = z.object({
    id: z.string(),
    referenceId: z.string(),
    status: z.string(),
    effectiveDate: z.string().nullable(),
    url: z.string().nullable(),
    sharedFile: z
        .object({
        id: z.string(),
        name: z.string(),
        mimeType: z.string(),
        size: z.number(),
        signedUrl: z.string(),
    })
        .nullable(),
});
export const AdminCreateVersionBodySchema = z.object({
    referenceId: z.string(),
    status: z.string(),
    effectiveDate: z.string().optional(),
    url: z.string().optional(),
});
export const AdminUpdateVersionBodySchema = z.object({
    status: z.string().optional(),
    effectiveDate: z.string().optional(),
    url: z.string().optional(),
});
// ── Admin Reference Detail (big query + bulk update) ──
export const AdminReferenceDetailVersionUpdateSchema = z.object({
    id: z.string(),
    updateType: z.string(),
    shortSummary: z.string().nullable(),
    summary: z.string().nullable(),
    publicationDate: z.string(),
    redlineStatus: z.string(),
});
export const AdminReferenceDetailVersionSchema = z.object({
    id: z.string(),
    status: z.string(),
    effectiveDate: z.string().nullable(),
    url: z.string().nullable(),
    sharedFile: z
        .object({
        id: z.string(),
        name: z.string(),
        mimeType: z.string(),
        size: z.number(),
        signedUrl: z.string(),
    })
        .nullable(),
    update: AdminReferenceDetailVersionUpdateSchema.nullable(),
});
export const AdminReferenceDetailResponseSchema = z.object({
    id: z.string(),
    externalId: z.string(),
    title: z.string(),
    type: z.string().nullable(),
    source: z.object({
        id: z.string(),
        title: z.string(),
        geography: z.string(),
        icon: z.string().nullable(),
    }),
    url: z.string().nullable(),
    description: z.string().nullable(),
    versions: z.array(AdminReferenceDetailVersionSchema),
});
export const UpdateAdminReferenceDetailVersionPatchSchema = z.object({
    id: z.string(),
    status: z.string().optional(),
    effectiveDate: z.string().nullable().optional(),
    url: z.string().nullable().optional(),
});
export const UpdateAdminReferenceDetailUpdatePatchSchema = z.object({
    id: z.string(),
    updateType: z.string().optional(),
    shortSummary: z.string().nullable().optional(),
    summary: z.string().nullable().optional(),
    publicationDate: z.string().optional(),
});
export const UpdateAdminReferenceDetailBodySchema = z.object({
    title: z.string().optional(),
    type: z.string().nullable().optional(),
    source: z.string().optional(),
    url: z.string().nullable().optional(),
    description: z.string().nullable().optional(),
    versions: z.array(UpdateAdminReferenceDetailVersionPatchSchema).optional(),
    updates: z.array(UpdateAdminReferenceDetailUpdatePatchSchema).optional(),
});
// ── Admin Update Workspace (unified) ──
export const AdminUpdateWorkspaceBodySchema = z.object({
    name: z.string().min(1).optional(),
    memberIds: z.array(z.string()).optional(),
    features: z
        .array(z.object({
        feature: FeatureNameSchema,
        status: FeatureStatusSchema,
    }))
        .optional(),
    tier: PlanTierSchema.optional(),
});
// ── Country (medtech regulatory data) ──
export const MedtechHeroSchema = z.object({
    overview: z.string().optional(),
    classificationSystem: z.string().optional(),
    competentAuthority: z.string().optional(),
});
export const MedtechSourceRefSchema = z.object({
    id: z.string(),
});
export const MedtechAgencySchema = z.object({
    title: z.string(),
    subtitle: z.string().optional(),
    description: z.string().optional(),
    link: z.string().optional(),
});
export const MedtechRegulationSchema = z.object({
    title: z.string(),
    description: z.string().optional(),
    link: z.string().optional(),
});
export const MedtechDeviceClassificationSchema = z.object({
    classes: z.array(z.string()),
    description: z.string().optional(),
    link: z.string().optional(),
});
export const MedtechRegulatoryDataSchema = z.object({
    hero: MedtechHeroSchema.optional(),
    sources: z.array(MedtechSourceRefSchema).optional(),
    agencies: z.array(MedtechAgencySchema).optional(),
    regulations: z.array(MedtechRegulationSchema).optional(),
    deviceClassificationMd: MedtechDeviceClassificationSchema.optional(),
    deviceClassificationIvd: MedtechDeviceClassificationSchema.optional(),
});
export const CountryItemSchema = z.object({
    id: z.string(),
    stringId: z.string(),
    medtechRegulatoryData: MedtechRegulatoryDataSchema.nullable(),
    createdAt: z.string(),
    updatedAt: z.string(),
});
export const AdminCreateCountryBodySchema = z.object({
    stringId: z.string(),
    medtechRegulatoryData: MedtechRegulatoryDataSchema.nullable().optional(),
});
export const AdminUpdateCountryBodySchema = z.object({
    medtechRegulatoryData: MedtechRegulatoryDataSchema.nullable().optional(),
});
// ── API Clients ──
export const ApiClientItemSchema = z.object({
    id: z.string(),
    name: z.string(),
    keyPrefix: z.string(),
    createdAt: z.string().datetime(),
    lastUsedAt: z.string().datetime().nullable(),
    workspaceIds: z.array(z.string()),
});
export const CreateApiClientBodySchema = z.object({
    name: z.string().min(1),
    workspaceIds: z.array(z.string()),
});
export const CreateApiClientResponseSchema = z.object({
    id: z.string(),
    name: z.string(),
    keyPrefix: z.string(),
    apiKey: z.string(),
    workspaceIds: z.array(z.string()),
});
export const UpdateApiClientBodySchema = z.object({
    name: z.string().min(1).optional(),
    workspaceIds: z.array(z.string()).optional(),
});
export const backOfficeContract = c.router({
    getAllUsers: {
        method: 'GET',
        path: '/admin/boUsers',
        summary: 'Get all users (admin)',
        responses: {
            200: z.array(BackOfficeUserProfileSchema),
            401: z.object({
                message: z.string(),
                statusCode: z.number(),
            }),
        },
    },
    getAllWorkspaces: {
        method: 'GET',
        path: '/admin/boWorkspaces',
        summary: 'Get all workspaces (admin)',
        responses: {
            200: z.array(BackOfficeWorkspaceSchema),
            401: z.object({
                message: z.string(),
                statusCode: z.number(),
            }),
        },
    },
    updateUserType: {
        method: 'POST',
        path: '/admin/boUsers/udpateType',
        summary: 'Update user type (admin)',
        body: UpdateUserTypeSchema,
        responses: {
            200: BackOfficeUserProfileSchema,
            401: z.object({
                message: z.string(),
                statusCode: z.number(),
            }),
            404: z.object({
                message: z.string(),
                statusCode: z.number(),
            }),
        },
    },
    createUser: {
        method: 'POST',
        path: '/admin/boUsers',
        summary: 'Create new user (admin)',
        body: CreateUserSchema,
        responses: {
            201: BackOfficeUserProfileSchema,
            400: z.object({
                message: z.string(),
                statusCode: z.number(),
            }),
            401: z.object({
                message: z.string(),
                statusCode: z.number(),
            }),
        },
    },
    updateUserWorkspaces: {
        method: 'POST',
        path: '/admin/boUsers/updateWorkspaces',
        summary: 'Update user workspace assignments (admin)',
        body: UpdateUserWorkspacesSchema,
        responses: {
            200: z.object({
                message: z.string(),
                userId: z.string(),
                workspaceCount: z.number(),
            }),
            401: z.object({
                message: z.string(),
                statusCode: z.number(),
            }),
            404: z.object({
                message: z.string(),
                statusCode: z.number(),
            }),
        },
    },
    deleteWorkspace: {
        method: 'DELETE',
        path: '/admin/boWorkspaces/:id',
        summary: 'Delete a workspace (admin)',
        body: c.type(),
        responses: {
            200: z.object({ message: z.string() }),
            401: z.object({
                message: z.string(),
                statusCode: z.number(),
            }),
            404: z.object({
                message: z.string(),
                statusCode: z.number(),
            }),
        },
    },
    bulkDeleteWorkspaces: {
        method: 'POST',
        path: '/admin/boWorkspaces/bulk-delete',
        summary: 'Bulk delete workspaces (admin)',
        body: BulkDeleteWorkspacesBodySchema,
        responses: {
            200: BulkDeleteWorkspacesResponseSchema,
            401: z.object({
                message: z.string(),
                statusCode: z.number(),
            }),
        },
    },
    bulkDeleteUsers: {
        method: 'POST',
        path: '/admin/boUsers/bulk-delete',
        summary: 'Bulk delete users (admin)',
        body: BulkDeleteUsersBodySchema,
        responses: {
            200: BulkDeleteUsersResponseSchema,
            401: z.object({
                message: z.string(),
                statusCode: z.number(),
            }),
        },
    },
    updateWorkspace: {
        method: 'PATCH',
        path: '/admin/boWorkspaces/:id',
        summary: 'Update a workspace (admin) - unified endpoint for tier, members, features',
        body: AdminUpdateWorkspaceBodySchema,
        responses: {
            200: BackOfficeWorkspaceSchema,
            401: z.object({
                message: z.string(),
                statusCode: z.number(),
            }),
            404: z.object({
                message: z.string(),
                statusCode: z.number(),
            }),
        },
    },
    // ── Admin Regulatory Watch ──
    listAdminReferences: {
        method: 'GET',
        path: '/admin/regulatory-watch/references',
        summary: 'List all regulatory references (admin)',
        query: AdminListReferencesQuerySchema,
        responses: {
            200: z.array(AdminReferenceItemSchema),
            401: z.object({ message: z.string(), statusCode: z.number() }),
        },
    },
    createAdminReference: {
        method: 'POST',
        path: '/admin/regulatory-watch/references',
        summary: 'Create a regulatory reference (admin)',
        body: AdminCreateReferenceBodySchema,
        responses: {
            200: AdminReferenceItemSchema,
            400: z.object({ message: z.string() }),
            401: z.object({ message: z.string(), statusCode: z.number() }),
        },
    },
    updateAdminReference: {
        method: 'PATCH',
        path: '/admin/regulatory-watch/references/:id',
        summary: 'Update a regulatory reference (admin)',
        body: AdminUpdateReferenceBodySchema,
        responses: {
            200: AdminReferenceItemSchema,
            400: z.object({ message: z.string() }),
            401: z.object({ message: z.string(), statusCode: z.number() }),
            404: z.object({ message: z.string() }),
        },
    },
    deleteAdminReference: {
        method: 'DELETE',
        path: '/admin/regulatory-watch/references/:id',
        summary: 'Delete a regulatory reference and its versions/updates (admin)',
        body: c.type(),
        responses: {
            200: z.object({ message: z.string() }),
            401: z.object({ message: z.string(), statusCode: z.number() }),
            404: z.object({ message: z.string() }),
        },
    },
    listAdminUpdates: {
        method: 'GET',
        path: '/admin/regulatory-watch/updates',
        summary: 'List all regulatory updates (admin)',
        query: AdminListUpdatesQuerySchema,
        responses: {
            200: z.array(AdminUpdateItemSchema),
            401: z.object({ message: z.string(), statusCode: z.number() }),
        },
    },
    createAdminUpdate: {
        method: 'POST',
        path: '/admin/regulatory-watch/updates',
        summary: 'Create a regulatory update (admin)',
        body: AdminCreateUpdateBodySchema,
        responses: {
            200: AdminUpdateItemSchema,
            400: z.object({ message: z.string() }),
            401: z.object({ message: z.string(), statusCode: z.number() }),
        },
    },
    getAdminUpdate: {
        method: 'GET',
        path: '/admin/regulatory-watch/updates/:id',
        summary: 'Get full detail of a regulatory update (admin)',
        responses: {
            200: AdminUpdateDetailResponseSchema,
            401: z.object({ message: z.string(), statusCode: z.number() }),
            404: z.object({ message: z.string() }),
        },
    },
    updateAdminUpdate: {
        method: 'PATCH',
        path: '/admin/regulatory-watch/updates/:id',
        summary: 'Update a regulatory update (admin)',
        body: AdminUpdateUpdateBodySchema,
        responses: {
            200: AdminUpdateItemSchema,
            400: z.object({ message: z.string() }),
            401: z.object({ message: z.string(), statusCode: z.number() }),
            404: z.object({ message: z.string() }),
        },
    },
    generateAdminRedline: {
        method: 'POST',
        path: '/admin/regulatory-watch/updates/:id/redline/generate',
        summary: 'Trigger redline generation for an update (admin)',
        body: c.type(),
        responses: {
            200: AdminGenerateRedlineResponseSchema,
            400: z.object({ message: z.string() }),
            401: z.object({ message: z.string(), statusCode: z.number() }),
            404: z.object({ message: z.string() }),
        },
    },
    generateAdminSummary: {
        method: 'POST',
        path: '/admin/regulatory-watch/updates/:id/summary/generate',
        summary: 'Trigger AI summary (re)generation for an update (admin)',
        body: c.type(),
        responses: {
            200: AdminGenerateSummaryResponseSchema,
            400: z.object({ message: z.string() }),
            401: z.object({ message: z.string(), statusCode: z.number() }),
            404: z.object({ message: z.string() }),
        },
    },
    deleteAdminUpdate: {
        method: 'DELETE',
        path: '/admin/regulatory-watch/updates/:id',
        summary: 'Delete a regulatory update (admin)',
        body: c.type(),
        responses: {
            200: z.object({ message: z.string() }),
            401: z.object({ message: z.string(), statusCode: z.number() }),
            404: z.object({ message: z.string() }),
        },
    },
    discardAdminUpdate: {
        method: 'POST',
        path: '/admin/regulatory-watch/updates/:id/discard',
        summary: 'Move a news update to discarded items (deletes update, creates DiscardedSourceItem)',
        body: c.type(),
        responses: {
            200: z.object({ message: z.string() }),
            400: z.object({ message: z.string() }),
            401: z.object({ message: z.string(), statusCode: z.number() }),
            404: z.object({ message: z.string() }),
        },
    },
    uploadAdminUpdateFile: {
        method: 'POST',
        path: '/admin/regulatory-watch/updates/:id/file',
        summary: 'Upload a file for an update (admin)',
        contentType: 'multipart/form-data',
        body: c.type(),
        responses: {
            200: AdminUpdateItemSchema,
            400: z.object({ message: z.string() }),
            401: z.object({ message: z.string(), statusCode: z.number() }),
            404: z.object({ message: z.string() }),
        },
    },
    deleteAdminUpdateFile: {
        method: 'DELETE',
        path: '/admin/regulatory-watch/updates/:id/file',
        summary: 'Delete the file from an update (admin)',
        body: c.type(),
        responses: {
            200: AdminUpdateItemSchema,
            401: z.object({ message: z.string(), statusCode: z.number() }),
            404: z.object({ message: z.string() }),
        },
    },
    listAdminVersionsForReference: {
        method: 'GET',
        path: '/admin/regulatory-watch/references/:id/versions',
        summary: 'List versions for a regulatory reference (admin)',
        responses: {
            200: z.array(AdminVersionItemSchema),
            401: z.object({ message: z.string(), statusCode: z.number() }),
        },
    },
    createAdminVersion: {
        method: 'POST',
        path: '/admin/regulatory-watch/versions',
        summary: 'Create a new version for a reference (admin)',
        body: AdminCreateVersionBodySchema,
        responses: {
            200: AdminVersionItemSchema,
            400: z.object({ message: z.string() }),
            401: z.object({ message: z.string(), statusCode: z.number() }),
            404: z.object({ message: z.string() }),
        },
    },
    updateAdminVersion: {
        method: 'PATCH',
        path: '/admin/regulatory-watch/versions/:id',
        summary: 'Update a regulatory version (admin)',
        body: AdminUpdateVersionBodySchema,
        responses: {
            200: AdminVersionItemSchema,
            400: z.object({ message: z.string() }),
            401: z.object({ message: z.string(), statusCode: z.number() }),
            404: z.object({ message: z.string() }),
        },
    },
    uploadAdminVersionFile: {
        method: 'POST',
        path: '/admin/regulatory-watch/versions/:id/file',
        summary: 'Upload a regulation file for a version (admin)',
        contentType: 'multipart/form-data',
        body: c.type(),
        responses: {
            200: AdminVersionItemSchema,
            400: z.object({ message: z.string() }),
            401: z.object({ message: z.string(), statusCode: z.number() }),
            404: z.object({ message: z.string() }),
        },
    },
    deleteAdminVersion: {
        method: 'DELETE',
        path: '/admin/regulatory-watch/versions/:id',
        summary: 'Delete a regulatory version (admin)',
        body: c.type(),
        responses: {
            200: z.object({ message: z.string() }),
            401: z.object({ message: z.string(), statusCode: z.number() }),
            404: z.object({ message: z.string() }),
        },
    },
    deleteAdminVersionFile: {
        method: 'DELETE',
        path: '/admin/regulatory-watch/versions/:id/file',
        summary: 'Delete the regulation file from a version (admin)',
        body: c.type(),
        responses: {
            200: AdminVersionItemSchema,
            401: z.object({ message: z.string(), statusCode: z.number() }),
            404: z.object({ message: z.string() }),
        },
    },
    // ── Admin Reference Detail ──
    getAdminReferenceDetail: {
        method: 'GET',
        path: '/admin/regulatory-watch/references/:id/detail',
        summary: 'Get full reference detail with versions and linked updates (admin)',
        responses: {
            200: AdminReferenceDetailResponseSchema,
            401: z.object({ message: z.string(), statusCode: z.number() }),
            404: z.object({ message: z.string() }),
        },
    },
    updateAdminReferenceDetail: {
        method: 'PATCH',
        path: '/admin/regulatory-watch/references/:id/detail',
        summary: 'Bulk update reference fields, version fields, and update fields (admin)',
        body: UpdateAdminReferenceDetailBodySchema,
        responses: {
            200: AdminReferenceDetailResponseSchema,
            400: z.object({ message: z.string() }),
            401: z.object({ message: z.string(), statusCode: z.number() }),
            404: z.object({ message: z.string() }),
        },
    },
    // ── Admin Regulatory Watch: Sources ──
    listAdminSources: {
        method: 'GET',
        path: '/admin/regulatory-watch/sources',
        summary: 'List all regulatory sources (admin)',
        responses: {
            200: z.array(RegulatorySourceItemSchema),
            401: z.object({ message: z.string(), statusCode: z.number() }),
        },
    },
    createAdminSource: {
        method: 'POST',
        path: '/admin/regulatory-watch/sources',
        summary: 'Create a regulatory source (admin)',
        body: AdminCreateSourceBodySchema,
        responses: {
            200: RegulatorySourceItemSchema,
            400: z.object({ message: z.string() }),
            401: z.object({ message: z.string(), statusCode: z.number() }),
        },
    },
    updateAdminSource: {
        method: 'PATCH',
        path: '/admin/regulatory-watch/sources/:id',
        summary: 'Update a regulatory source (admin)',
        body: AdminUpdateSourceBodySchema,
        responses: {
            200: RegulatorySourceItemSchema,
            400: z.object({ message: z.string() }),
            401: z.object({ message: z.string(), statusCode: z.number() }),
            404: z.object({ message: z.string() }),
        },
    },
    deleteAdminSource: {
        method: 'DELETE',
        path: '/admin/regulatory-watch/sources/:id',
        summary: 'Delete a regulatory source (admin)',
        body: c.type(),
        responses: {
            200: z.object({ message: z.string() }),
            400: z.object({ message: z.string() }),
            401: z.object({ message: z.string(), statusCode: z.number() }),
            404: z.object({ message: z.string() }),
        },
    },
    uploadSourceIcon: {
        method: 'POST',
        path: '/admin/regulatory-watch/sources/:id/icon',
        summary: 'Upload an icon image for a source (admin)',
        contentType: 'multipart/form-data',
        body: c.type(),
        responses: {
            200: RegulatorySourceItemSchema,
            400: z.object({ message: z.string() }),
            401: z.object({ message: z.string(), statusCode: z.number() }),
            404: z.object({ message: z.string() }),
        },
    },
    // ── Admin Regulatory Watch: Institutions ──
    listInstitutions: {
        method: 'GET',
        path: '/admin/regulatory-watch/institutions',
        summary: 'List all institutions (admin)',
        responses: {
            200: z.array(InstitutionItemSchema),
            401: z.object({ message: z.string(), statusCode: z.number() }),
        },
    },
    createInstitution: {
        method: 'POST',
        path: '/admin/regulatory-watch/institutions',
        summary: 'Create an institution (admin)',
        body: AdminCreateInstitutionBodySchema,
        responses: {
            200: InstitutionItemSchema,
            400: z.object({ message: z.string() }),
            401: z.object({ message: z.string(), statusCode: z.number() }),
        },
    },
    updateInstitution: {
        method: 'PATCH',
        path: '/admin/regulatory-watch/institutions/:id',
        summary: 'Update an institution (admin)',
        body: AdminUpdateInstitutionBodySchema,
        responses: {
            200: InstitutionItemSchema,
            401: z.object({ message: z.string(), statusCode: z.number() }),
            404: z.object({ message: z.string() }),
        },
    },
    deleteInstitution: {
        method: 'DELETE',
        path: '/admin/regulatory-watch/institutions/:id',
        summary: 'Delete an institution (admin)',
        body: c.type(),
        responses: {
            200: z.object({ message: z.string() }),
            400: z.object({ message: z.string() }),
            401: z.object({ message: z.string(), statusCode: z.number() }),
            404: z.object({ message: z.string() }),
        },
    },
    // ── Source Scraping (interactive discovery + confirm) ──
    startSourceScraping: {
        method: 'POST',
        path: '/admin/source-scraping/start',
        summary: 'Start source discovery workflow',
        body: StartSourceScrapingBodySchema,
        responses: {
            200: StartSourceScrapingResponseSchema,
            400: z.object({ message: z.string() }),
            401: z.object({ message: z.string(), statusCode: z.number() }),
        },
    },
    getSourceScrapingStatus: {
        method: 'GET',
        path: '/admin/source-scraping/:workflowId/status',
        summary: 'Get source discovery workflow progress and results',
        responses: {
            200: GetSourceScrapingStatusResponseSchema,
            401: z.object({ message: z.string(), statusCode: z.number() }),
            404: z.object({ message: z.string() }),
        },
    },
    confirmSourceScraping: {
        method: 'POST',
        path: '/admin/source-scraping/confirm',
        summary: 'Persist admin-reviewed sources and references',
        body: ConfirmSourceScrapingBodySchema,
        responses: {
            200: ConfirmSourceScrapingResponseSchema,
            400: z.object({ message: z.string() }),
            401: z.object({ message: z.string(), statusCode: z.number() }),
        },
    },
    cancelSourceScraping: {
        method: 'POST',
        path: '/admin/source-scraping/:workflowId/cancel',
        summary: 'Cancel a running source discovery workflow',
        body: c.type(),
        responses: {
            200: z.object({ message: z.string() }),
            401: z.object({ message: z.string(), statusCode: z.number() }),
            404: z.object({ message: z.string() }),
        },
    },
    // ── Discarded Source Items ──
    listDiscardedItems: {
        method: 'GET',
        path: '/admin/regulatory-watch/discarded-items',
        summary: 'List all discarded source items (admin)',
        query: ListDiscardedItemsQuerySchema,
        responses: {
            200: z.array(DiscardedSourceItemResponseSchema),
            401: z.object({ message: z.string(), statusCode: z.number() }),
        },
    },
    // ── Countries (medtech regulatory data) ──
    listCountries: {
        method: 'GET',
        path: '/admin/regulatory-watch/countries',
        summary: 'List all countries with medtech regulatory data (admin)',
        responses: {
            200: z.array(CountryItemSchema),
            401: z.object({ message: z.string(), statusCode: z.number() }),
        },
    },
    createCountry: {
        method: 'POST',
        path: '/admin/regulatory-watch/countries',
        summary: 'Create a country entry (admin)',
        body: AdminCreateCountryBodySchema,
        responses: {
            200: CountryItemSchema,
            400: z.object({ message: z.string() }),
            401: z.object({ message: z.string(), statusCode: z.number() }),
        },
    },
    getCountry: {
        method: 'GET',
        path: '/admin/regulatory-watch/countries/:id',
        summary: 'Get a country by id (admin)',
        responses: {
            200: CountryItemSchema,
            401: z.object({ message: z.string(), statusCode: z.number() }),
            404: z.object({ message: z.string() }),
        },
    },
    updateCountry: {
        method: 'PATCH',
        path: '/admin/regulatory-watch/countries/:id',
        summary: 'Update a country medtech regulatory data (admin)',
        body: AdminUpdateCountryBodySchema,
        responses: {
            200: CountryItemSchema,
            401: z.object({ message: z.string(), statusCode: z.number() }),
            404: z.object({ message: z.string() }),
        },
    },
    deleteCountry: {
        method: 'DELETE',
        path: '/admin/regulatory-watch/countries/:id',
        summary: 'Delete a country entry (admin)',
        body: c.type(),
        responses: {
            200: z.object({ message: z.string() }),
            401: z.object({ message: z.string(), statusCode: z.number() }),
            404: z.object({ message: z.string() }),
        },
    },
    // ── Consultant Organizations ──
    listConsultantOrgs: {
        method: 'GET',
        path: '/admin/consultant-orgs',
        summary: 'List all consultant organizations (admin)',
        responses: {
            200: z.array(ConsultantOrganizationItemSchema),
            401: z.object({ message: z.string(), statusCode: z.number() }),
        },
    },
    createConsultantOrg: {
        method: 'POST',
        path: '/admin/consultant-orgs',
        summary: 'Create a consultant organization (admin)',
        body: AdminCreateConsultantOrgBodySchema,
        responses: {
            200: ConsultantOrganizationItemSchema,
            400: z.object({ message: z.string() }),
            401: z.object({ message: z.string(), statusCode: z.number() }),
        },
    },
    updateConsultantOrg: {
        method: 'PATCH',
        path: '/admin/consultant-orgs/:id',
        summary: 'Update a consultant organization (admin)',
        body: AdminUpdateConsultantOrgBodySchema,
        responses: {
            200: ConsultantOrganizationItemSchema,
            401: z.object({ message: z.string(), statusCode: z.number() }),
            404: z.object({ message: z.string() }),
        },
    },
    deleteConsultantOrg: {
        method: 'DELETE',
        path: '/admin/consultant-orgs/:id',
        summary: 'Delete a consultant organization (admin)',
        body: c.type(),
        responses: {
            200: z.object({ message: z.string() }),
            401: z.object({ message: z.string(), statusCode: z.number() }),
            404: z.object({ message: z.string() }),
        },
    },
    uploadConsultantOrgLogo: {
        method: 'POST',
        path: '/admin/consultant-orgs/:id/logo',
        summary: 'Upload a logo for a consultant organization (admin)',
        contentType: 'multipart/form-data',
        body: c.type(),
        responses: {
            200: ConsultantOrganizationItemSchema,
            400: z.object({ message: z.string() }),
            401: z.object({ message: z.string(), statusCode: z.number() }),
            404: z.object({ message: z.string() }),
        },
    },
    // ── API Clients ──
    listApiClients: {
        method: 'GET',
        path: '/admin/api-clients',
        summary: 'List all API clients (admin)',
        responses: {
            200: z.array(ApiClientItemSchema),
            401: z.object({ message: z.string(), statusCode: z.number() }),
        },
    },
    createApiClient: {
        method: 'POST',
        path: '/admin/api-clients',
        summary: 'Create an API client (admin)',
        body: CreateApiClientBodySchema,
        responses: {
            201: CreateApiClientResponseSchema,
            400: z.object({ message: z.string() }),
            401: z.object({ message: z.string(), statusCode: z.number() }),
        },
    },
    updateApiClient: {
        method: 'PATCH',
        path: '/admin/api-clients/:id',
        summary: 'Update an API client (admin)',
        body: UpdateApiClientBodySchema,
        responses: {
            200: ApiClientItemSchema,
            401: z.object({ message: z.string(), statusCode: z.number() }),
            404: z.object({ message: z.string() }),
        },
    },
    deleteApiClient: {
        method: 'DELETE',
        path: '/admin/api-clients/:id',
        summary: 'Delete an API client (admin)',
        body: z.undefined(),
        responses: {
            200: z.object({ message: z.string() }),
            401: z.object({ message: z.string(), statusCode: z.number() }),
            404: z.object({ message: z.string() }),
        },
    },
});
//# sourceMappingURL=backoffice.js.map